/**
 * CLE-2661 — pVerify practice type codes offered in the Service Type dropdown.
 *
 * A practice type code selects the SET of service types pVerify asks the payer about. The numbers are
 * easy to confuse with the X12 service type codes that appear inside pVerify's own labels, and getting
 * them backwards is silent: this list used to offer "ASC Facility" as `13`, which is the SERVICE type
 * for ASC but resolves as a PRACTICE type to Hospital-InPatient. Every response to it came back
 * without an ASC block at all — strictly less ASC data than the default option returns. The practice
 * type code for a plain ASC inquiry is `28`; since CLE-3088 "ASC Facility" sends `236` (ASC + Hospital
 * Outpatient) instead, and `28` is a legacy alias of it.
 *
 * Kept short on purpose. pVerify publishes 158 codes; a dropdown of mostly-irrelevant options is how
 * the wrong one gets picked. Mirrors PRACTICE_TYPE_CATALOG in the API's eligibility domain — keep the
 * two in step.
 *
 * CLE-2675 (2026-08-25) — these entries say "Surgical (Physician Benefit)" for pVerify's
 * `Surgical - Office (2)`. It replaced "Physician Outpatient", which collided with the benefits
 * tab of that name (a different block) and cost the estimator an afternoon. Ani's wording, and
 * deliberately the SAME wording as the tab and the account benefit-priority setting — one name,
 * one benefit. See the fuller note on PRACTICE_TYPE_CATALOG in the API.
 */

/**
 * The historical global default: ASC Facility (13) + Hospital-InPatient (48) +
 * Hospital-OutPatient (50) + Surgical-Office (2), all in one inquiry. Used when the account has no
 * `defaultPracticeTypeCode` of its own.
 */
export const DEFAULT_PRACTICE_TYPE_CODE = "189";

export const mapPayerNameToCodeForDropdown: { name: string; value: string }[] =
  [
    {
      name: "All Other Medical (ASC + Hospital In/Outpatient + Surgical (Physician Benefit))",
      value: "189",
    },
    {
      name: "Hospital Outpatient + Surgical (Physician Benefit)",
      value: "235",
    },
    { name: "ASC Facility + Surgical (Physician Benefit)", value: "185" },
    // CLE-2675 — Specialist Office (98) + Surgical (2) together. Office-based accounts on the
    // payer-routed benefit priority need both blocks on one response: the office block for
    // Medicare Advantage, the Surgical block for commercial. Code 18 returns only the first.
    { name: "Specialist Office + Surgical (Physician Benefit)", value: "43" },
    // CLE-3050 — 43 plus MRI/CAT Scan (62) and X-Ray (4), minted by pVerify for us (2026-10-30), so a
    // clinic visit with an office visit and an imaging code gets both copays from one check.
    {
      name: "Specialist Office + Surgical (Physician Benefit) + X-Ray + MRI/CAT Scan",
      value: "237",
    },
    // CLE-3088 — ASC Facility (13) + Hospital-OutPatient (50), minted by pVerify for us (2026-11-04). Ani:
    // replace the option's code, don't add one. It was 28 (ASC alone), which from many payers returned no
    // Hospital Outpatient data for the co-insurance substitute to borrow. A stored 28 is aliased below.
    { name: "ASC Facility", value: "236" },
    { name: "Hospital Outpatient", value: "83" },
    // CLE-3024 — Ani, 2026-10-22: standalone Hospital Inpatient (until now only inside 189). pVerify's
    // practice type for Hospital-InPatient (48) is 73 (REST Eligibility Summary API 4.1, section
    // 3.2). CLE-2989 had treated 73 as a mistaken Diagnostic Medical code; it was this all along.
    { name: "Hospital Inpatient", value: "73" },
    { name: "Specialist - Office", value: "18" },
    { name: "X-Ray + MRI/CAT Scan", value: "46" },
    { name: "MRI/CAT Scan", value: "31" },
    // CLE-2755 — Ani, 2026-09-08. Lets an estimator mark an order as a diagnostic study rather
    // than a procedure. On Lincoln's BCBS Medicare Advantage TN/AL that selection is what drops
    // the quoted copay from the $325 procedure rate to the $100 diagnostic one; the API rule is
    // `getBcbsDiagnosticCopayOverride`. CLE-2989 — pVerify's practice type is 113. This was 73 —
    // the X12 SERVICE type for Diagnostic Medical — which pVerify ran as Hospital-InPatient,
    // because 73 is the Hospital Inpatient practice type (offered above since CLE-3024).
    { name: "Diagnostic Medical", value: "113" },
  ];

/**
 * CLE-2989 — codes we once sent, mapped to the code sent for the same choice today. Mirrors
 * LEGACY_PRACTICE_TYPE_ALIASES in the API's practiceTypes.ts — keep the two in step.
 *
 * CLE-3088 — holds 28 → 236: "ASC Facility" had one meaning and now sends 236 (ASC + Hospital
 * Outpatient). The API's cle3088 migration rewrites stored 28s on orders and account defaults; this
 * catches anything it missed, so a stored "28" shows "ASC Facility" selected and sends 236 rather than
 * leaving the dropdown blank. (History: CLE-2989's 73 → 113 was retired by CLE-3024 — 73 is Hospital
 * Inpatient.)
 *
 * Not for 13 (the ASC mistake): that one is retired and falls back to the account default. An alias is
 * right only where the old code had one unambiguous meaning and no option is offered under it.
 */
export const LEGACY_PRACTICE_TYPE_ALIASES: Readonly<Record<string, string>> =
  Object.freeze({ "28": "236" });

/** The code `code` is now sent as: its alias target when it is a legacy code, else itself. Exact match. */
export const normalizePracticeTypeCode = (
  code?: string | null
): string | null | undefined =>
  code &&
  Object.prototype.hasOwnProperty.call(LEGACY_PRACTICE_TYPE_ALIASES, code)
    ? LEGACY_PRACTICE_TYPE_ALIASES[code]
    : code;

/**
 * The practice type an eligibility call should default to: the account's own setting when it has one,
 * otherwise the historical global default. Every account starts with none, so nothing changes until an
 * account is moved over deliberately. CLE-3088 — a legacy account default (28) comes back as the code it
 * is sent as (236), so the dropdown shows it selected.
 */
export const getDefaultPracticeTypeCode = (
  accountDefaultPracticeTypeCode?: string | null
): string =>
  normalizePracticeTypeCode(accountDefaultPracticeTypeCode) ||
  DEFAULT_PRACTICE_TYPE_CODE;

const KNOWN_PRACTICE_TYPE_CODES = new Set(
  mapPayerNameToCodeForDropdown.map((option) => option.value)
);

/** A code the dropdown offers, matched exactly: a legacy 28 is not one (normalise it first). */
export const isKnownPracticeTypeCode = (code?: string | null): boolean =>
  !!code && KNOWN_PRACTICE_TYPE_CODES.has(code);

/** CLE-3088 — the dropdown label for a stored code, legacy codes included; null when the dropdown does not offer it. */
export const practiceTypeOptionName = (code?: string | null): string | null =>
  mapPayerNameToCodeForDropdown.find(
    (option) => option.value === normalizePracticeTypeCode(code)
  )?.name ?? null;

/**
 * CLE-2661 — a practice type code carried over from an existing order, or null when that code is no
 * longer one we offer.
 *
 * Redo / copy / draft seed the Service Type from the order's own recorded code so a repeat inquiry
 * asks the same question the original did. That is right for codes we still offer and wrong for the
 * ones we removed: `13` in particular is not a practice type at all — pVerify resolves it to
 * Hospital-InPatient and answers with the hospital ambulatory-surgical benefit rather than the
 * freestanding-ASC one. Roughly 370 production orders carry it.
 *
 * Carrying such a code forward is worse than merely stale, because the estimator cannot correct it:
 * a PrimeReact Dropdown holding a value absent from its options renders as the empty placeholder, so
 * the form looks blank while silently still holding the bad code. Returning null instead lets
 * PrimaryInsuranceSection / SecondaryInsuranceSection fill in the account's default, which is what a
 * brand-new order on that account would use.
 *
 * Scoped to the SPA on purpose. The monthly re-run still re-asks with the order's own recorded code
 * (`buildEligibilityRequestForOrder`), because changing the question for an estimate a patient has
 * already been sent moves their quote — a deliberate call, tracked separately. A redo or copy is a
 * NEW estimate, which is exactly the case CLE-2661 asks us not to create with the wrong code.
 *
 * CLE-2989 — a legacy code with an alias is NOT dropped: it is returned as its alias target, because
 * unlike 13 it had exactly one meaning and the estimator chose it. (CLE-3088: 28 comes back as 236.)
 */
export const knownPracticeTypeCodeOrNull = (
  code?: string | null
): string | null => {
  const normalized = normalizePracticeTypeCode(code);
  return isKnownPracticeTypeCode(normalized) ? (normalized as string) : null;
};
